import { css } from '@emotion/react'
import { Cell, Grid } from 'bold-ui'
import { CheckboxField, DateField, MonthField } from 'components/form'
import { RastreioCancerPerguntaOpcaoEnum } from 'graphql/types.generated'
import { CidadaoAtendimentoContextModel } from 'hooks/atendimento-context/model-atendimentoContext'
import { useField } from 'react-final-form'
import { dateToReferenceMonth } from 'util/date/converter'
import { toDate } from 'util/date/formatDate'

import { ExamePreventivoRadioField } from './ExamePreventivoRadioField'
import { CitopatologicoPerguntaField } from './model-citopatologico'
import { MotivoExameRadioField } from './MotivoExameRadioField'
import { SangramentoMenopausaRadioField } from './SangramentoMenopausaRadioField'
import { SangramentoRelacaoSexualRadioField } from './SangramentoRelacaoSexualRadioField'
import { SimNaoNaoSabeRadioField } from './SimNaoNaoSabeRadioField'
import { SituacaoEspecialRadioField } from './SituacaoEspecialRadioField'

const IDADE_MAXIMA = 64
const IDADE_MINIMA = 25

interface CitopatologicoAnamneseModalStepProps {
  cidadao: CidadaoAtendimentoContextModel
  serverTime: Date
}

export function CitopatologicoAnamneseModalStep(props: Readonly<CitopatologicoAnamneseModalStepProps>) {
  const { cidadao, serverTime } = props

  const idade = cidadao.idadeEmAnos
  const showSituacaoEspecial = idade < IDADE_MINIMA || idade > IDADE_MAXIMA
  const showNuncaFezPreventivo = idade > IDADE_MAXIMA

  const dataNascimentoAsDate = toDate(cidadao.dataNascimento)

  const {
    input: { value: examePreventivo },
  } = useField<RastreioCancerPerguntaOpcaoEnum>(CitopatologicoPerguntaField.examePreventivo, {
    subscription: { value: true },
  })

  const {
    input: { value: checkDum },
  } = useField<boolean>('checkDum', {
    subscription: { value: true },
  })

  const showDataPreventivo = examePreventivo === RastreioCancerPerguntaOpcaoEnum.SIM_PAPANICOLAU

  const { positionCheckbox } = createStyles()
  return (
    <Grid>
      <Cell size={12} data-testid='motivoExame'>
        <MotivoExameRadioField name={CitopatologicoPerguntaField.motivoExame} required />
      </Cell>
      {showSituacaoEspecial && (
        <Cell size={12} data-testid='situacaoEspecial'>
          <SituacaoEspecialRadioField
            name={CitopatologicoPerguntaField.situacaoEspecial}
            showNuncaFezPreventivo={showNuncaFezPreventivo}
            clearable
          />
        </Cell>
      )}
      <Cell size={12} data-testid='examePreventivo'>
        <ExamePreventivoRadioField name={CitopatologicoPerguntaField.examePreventivo} required />
      </Cell>
      {showDataPreventivo && (
        <Cell size={8} data-testid='dataPreventivo'>
          <MonthField
            name={CitopatologicoPerguntaField.dataPreventivo}
            label='Mês e ano em que realizou o último preventivo?'
            required
            minMonth={dateToReferenceMonth(dataNascimentoAsDate) ?? undefined}
            maxMonth={dateToReferenceMonth(serverTime) ?? undefined}
          />
        </Cell>
      )}
      <Cell size={12} data-testid='diu'>
        <SimNaoNaoSabeRadioField name={CitopatologicoPerguntaField.diu} label='Usa DIU?' clearable />
      </Cell>
      <Cell size={12} data-testid='estaGravida'>
        <SimNaoNaoSabeRadioField name={CitopatologicoPerguntaField.estaGravida} label='Está gravida?' clearable />
      </Cell>
      <Cell size={12} data-testid='usaPilulaAnticoncepcional'>
        <SimNaoNaoSabeRadioField
          name={CitopatologicoPerguntaField.usaPilulaAnticoncepcional}
          label='Usa pílula anticoncepcional?'
          required
        />
      </Cell>
      <Cell size={12} data-testid='usaHormonio'>
        <SimNaoNaoSabeRadioField
          name={CitopatologicoPerguntaField.usaHormonio}
          label='Usa hormônio/remédio para tratar a menopausa?'
          required
        />
      </Cell>
      <Cell size={12} data-testid='radioterapia'>
        <SimNaoNaoSabeRadioField
          name={CitopatologicoPerguntaField.radioterapia}
          label='Fez tratamento de radioterapia?'
          clearable
        />
      </Cell>

      <Cell size={6} data-testid='dum'>
        <DateField
          name={CitopatologicoPerguntaField.dum}
          label='Data da última menstruação'
          required={!checkDum}
          disabled={checkDum}
          minDate={dataNascimentoAsDate ?? undefined}
          maxDate={serverTime}
        />
      </Cell>
      <Cell size={6} style={positionCheckbox} data-testid='checkDum'>
        <CheckboxField name='checkDum' label='Não lembra/não sabe' />
      </Cell>

      <Cell size={12} data-testid='sangramentoPosRelacao'>
        <SangramentoRelacaoSexualRadioField name={CitopatologicoPerguntaField.sangramentoPosRelacao} required />
      </Cell>
      <Cell size={12} data-testid='sangramentoPosMenopausa'>
        <SangramentoMenopausaRadioField name={CitopatologicoPerguntaField.sangramentoPosMenopausa} />
      </Cell>
    </Grid>
  )
}

const createStyles = () => ({
  positionCheckbox: css`
    padding-top: 2.5rem;
    align-self: flex-start;
  `,
})
